import { useEffect } from "react";
import { gql } from "@apollo/client";
import { DetailHero, Feature, GridInductees, SEO, Title } from "@components";
import { PressReleaseQuery } from "@graphqlTypes";
import { FaustTemplate } from "@faustwp/core";
import { HIGHLIGHT_COLORS } from "@/core/constants";
import { usePageHighlight } from "@/providers/PageHighlightContext";
import { FlexiblePageContentFragment } from "@/fragments/FlexibleContent";
import { FlexibleContentMap } from "@/components/ComponentMaps";
import { getBasicQueryVars, getPageTitle } from "@/core/utils";
import { usePageLoading } from "@/providers/LoadingContext";
import { useTrackRouteChange } from "@/providers/TrackingContext";
import { usePageWrapperData } from "@/providers/PageWrapperContext";

const PressRelease: FaustTemplate<PressReleaseQuery> = ({ data, loading, pageWrapperData }: FaustPageProps<PressReleaseQuery>) => {
  const { setHighlightColor } = usePageHighlight();
  const { recentStories } = pageWrapperData;
  const { previewLoading, setPreviewLoading } = usePageLoading();
  const { featuredInductees, siteSettings, siteTitle } = usePageWrapperData();
  const {
    title,
    flexiblePageContent,
    pressReleaseFields: {
      publishDate,
      ogTitleOverride: ogTitle,
      ogDescription,
    } = {},
  } = data?.rhPressRelease || {};

  const {
    pressReleaseFeature,
    pressReleaseHeroImage,
    pressReleaseTitle,
    pressReleaseInducteesHeadline,
    pressReleaseInducteesCta
  } = siteSettings || {};
  const seoTitle = getPageTitle(siteTitle, ogTitle || title);
  
  useEffect(() => {
    setHighlightColor(HIGHLIGHT_COLORS.GREEN);
  }, []);
  useTrackRouteChange('brand');

  useEffect(() => {
    if (!loading && previewLoading) setPreviewLoading(false);
  }, [loading, previewLoading]);

  if (loading) return;
  return (
    <>
      <SEO title={seoTitle} description={ogDescription} image={pressReleaseHeroImage} />
      <DetailHero
        publishDate={publishDate}
        image={pressReleaseHeroImage}
        headline={pressReleaseTitle}
      />
      { flexiblePageContent?.content && (
        <FlexibleContentMap content={flexiblePageContent.content} recentStories={recentStories} />
      )}
      { pressReleaseFeature?.headline && <Title headline={pressReleaseFeature.headline || ''} /> }
      { pressReleaseFeature?.items && <Feature items={pressReleaseFeature.items || []} float={false} /> }
      { featuredInductees?.nodes && <GridInductees headline={pressReleaseInducteesHeadline} cta={pressReleaseInducteesCta} inductees={featuredInductees.nodes} /> }
    </>
  );
};

PressRelease.query = gql`
  ${FlexiblePageContentFragment}
  query PressRelease($databaseId: ID!, $asPreview: Boolean = false, $language: String!) {
    rhPressRelease(id: $databaseId, asPreview: $asPreview, language: $language) {
      title
      pressReleaseFields {
        publishDate
        ogTitleOverride
        ogDescription
      }
      flexiblePageContent {
        ...ContentFragment
      }
    }
  }
`;

PressRelease.variables = getBasicQueryVars;

export default PressRelease;
